'use client';
import React, { useState, useCallback } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  ExternalLink,
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  BookMarked,
  Link2,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface SuggestedCitation {
  title: string;
  author: string;
  url: string;
  type: string;
  credibilityScore: number;
}

interface Claim {
  id: string;
  text: string;
  status: 'verified' | 'unverified' | 'disputed' | 'oral_tradition';
  explanation: string;
  suggestedCitations: SuggestedCitation[];
}

interface AutoHyperlink {
  phrase: string;
  url: string;
  sourceTitle: string;
}

interface AnalysisResult {
  overallAssessment: 'verified' | 'partially_verified' | 'unverified' | 'oral_tradition';
  summary: string;
  claims: Claim[];
  autoHyperlinks: AutoHyperlink[];
}

interface CitationAuthenticatorProps {
  title: string;
  body: string;
  era: string;
  topic: string;
  onApplyCitation: (citation: { title: string; author: string; url: string; documentType: string }) => void;
}

const statusConfig = {
  verified: {
    icon: CheckCircle2,
    label: 'Verified',
    color: '#2C5E3B',
    bg: '#2C5E3B18',
    border: '#2C5E3B44',
  },
  unverified: {
    icon: HelpCircle,
    label: 'Unverified',
    color: '#A06800',
    bg: '#F39C1218',
    border: '#F39C1244',
  },
  disputed: {
    icon: AlertTriangle,
    label: 'Disputed',
    color: '#B03020',
    bg: '#E05A4718',
    border: '#E05A4744',
  },
  oral_tradition: {
    icon: BookMarked,
    label: 'Oral Tradition',
    color: '#6B5B95',
    bg: '#6B5B9518',
    border: '#6B5B9544',
  },
};

const overallConfig = {
  verified: { icon: ShieldCheck, label: 'Well Sourced', color: '#2C5E3B', bg: '#2C5E3B18', border: '#2C5E3B44' },
  partially_verified: { icon: ShieldQuestion, label: 'Partially Verified', color: '#A06800', bg: '#F39C1218', border: '#F39C1244' },
  unverified: { icon: ShieldAlert, label: 'Needs Sources', color: '#B03020', bg: '#E05A4718', border: '#E05A4744' },
  oral_tradition: { icon: BookMarked, label: 'Oral Tradition', color: '#6B5B95', bg: '#6B5B9518', border: '#6B5B9544' },
};

export default function CitationAuthenticator({
  title,
  body,
  era,
  topic,
  onApplyCitation,
}: CitationAuthenticatorProps) {
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedClaims, setExpandedClaims] = useState<Set<string>>(new Set());
  const [appliedCitations, setAppliedCitations] = useState<Set<string>>(new Set());

  const analyze = useCallback(async () => {
    const plainText = body.replace(/<[^>]+>/g, ' ').trim();
    if (plainText.length < 50) {
      setError('Please write at least 50 words before running analysis.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch('/api/ai/citation-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, body, era, topic }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Analysis failed. Please try again.');
        return;
      }

      setResult(data);
      // Auto-expand unverified/disputed claims
      const autoExpand = new Set<string>();
      data.claims?.forEach((c: Claim) => {
        if (c.status === 'unverified' || c.status === 'disputed') {
          autoExpand.add(c.id);
        }
      });
      setExpandedClaims(autoExpand);
    } catch (err: any) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  }, [title, body, era, topic]);

  const toggleClaim = (id: string) => {
    setExpandedClaims((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApplyCitation = (citation: SuggestedCitation, claimId: string) => {
    const key = `${claimId}-${citation.url}`;
    setAppliedCitations((prev) => new Set(prev).add(key));
    onApplyCitation({
      title: citation.title,
      author: citation.author,
      url: citation.url,
      documentType: citation.type,
    });
  };

  const plainBodyLength = body.replace(/<[^>]+>/g, ' ').trim().length;

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-4 border-b"
        style={{ borderColor: 'var(--border)', backgroundColor: '#C17A3E08' }}
      >
        <div className="flex items-center gap-2.5">
          <Sparkles size={16} style={{ color: 'var(--primary)' }} />
          <div>
            <h2 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
              AI Citation Authenticator
            </h2>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Powered by OpenAI — analyzes claims and suggests verified sources
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={analyze}
          disabled={isLoading || plainBodyLength < 50}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
        >
          {isLoading ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Analyzing…
            </>
          ) : (
            <>
              <ShieldCheck size={14} />
              {result ? 'Re-analyze' : 'Authenticate'}
            </>
          )}
        </button>
      </div>

      {/* Body */}
      <div className="px-6 py-5">
        {/* Idle state */}
        {!result && !isLoading && !error && (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <ShieldQuestion size={32} style={{ color: 'var(--muted-foreground)' }} className="mb-3 opacity-50" />
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--foreground)' }}>
              Authenticate your submission
            </p>
            <p className="text-xs max-w-sm" style={{ color: 'var(--muted-foreground)' }}>
              Click <strong>Authenticate</strong> to have OpenAI scan your content for verifiable claims,
              flag unverified statements, and suggest credible citations with hyperlinks.
            </p>
            {plainBodyLength < 50 && (
              <p className="text-xs mt-3 px-3 py-1.5 rounded-lg" style={{ backgroundColor: '#F39C1218', color: '#A06800' }}>
                Write at least 50 characters to enable analysis
              </p>
            )}
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Loader2 size={28} className="animate-spin mb-3" style={{ color: 'var(--primary)' }} />
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--foreground)' }}>
              Analyzing your submission…
            </p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Scanning claims, cross-referencing sources, generating citations
            </p>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div
            className="flex items-start gap-2 px-4 py-3 rounded-lg border text-sm"
            style={{ backgroundColor: '#E05A4711', borderColor: '#E05A4744', color: '#B03020' }}
          >
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {/* Results */}
        {result && !isLoading && (
          <div className="space-y-5">
            {/* Overall assessment banner */}
            {(() => {
              const cfg = overallConfig[result.overallAssessment] || overallConfig.unverified;
              const Icon = cfg.icon;
              return (
                <div
                  className="flex items-start gap-3 px-4 py-3 rounded-lg border"
                  style={{ backgroundColor: cfg.bg, borderColor: cfg.border }}
                >
                  <Icon size={18} style={{ color: cfg.color }} className="shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold mb-0.5" style={{ color: cfg.color }}>
                      {cfg.label}
                    </p>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                      {result.summary}
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Claims list */}
            {result.claims && result.claims.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--muted-foreground)' }}>
                  Claims Identified ({result.claims.length})
                </h3>
                <div className="space-y-2">
                  {result.claims.map((claim) => {
                    const cfg = statusConfig[claim.status] || statusConfig.unverified;
                    const Icon = cfg.icon;
                    const isExpanded = expandedClaims.has(claim.id);

                    return (
                      <div
                        key={claim.id}
                        className="rounded-lg border overflow-hidden"
                        style={{ borderColor: cfg.border, backgroundColor: cfg.bg }}
                      >
                        <button
                          type="button"
                          onClick={() => toggleClaim(claim.id)}
                          className="w-full flex items-start gap-3 px-4 py-3 text-left transition-opacity duration-150 hover:opacity-80"
                        >
                          <Icon size={14} style={{ color: cfg.color }} className="shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span
                                className="text-xs font-semibold px-1.5 py-0.5 rounded"
                                style={{ backgroundColor: cfg.border, color: cfg.color }}
                              >
                                {cfg.label}
                              </span>
                              {claim.suggestedCitations?.length > 0 && (
                                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                                  {claim.suggestedCitations.length} source{claim.suggestedCitations.length !== 1 ? 's' : ''} suggested
                                </span>
                              )}
                            </div>
                            <p className="text-xs leading-relaxed line-clamp-2" style={{ color: 'var(--foreground)' }}>
                              &ldquo;{claim.text}&rdquo;
                            </p>
                          </div>
                          {isExpanded ? (
                            <ChevronUp size={14} style={{ color: 'var(--muted-foreground)' }} className="shrink-0 mt-0.5" />
                          ) : (
                            <ChevronDown size={14} style={{ color: 'var(--muted-foreground)' }} className="shrink-0 mt-0.5" />
                          )}
                        </button>

                        {isExpanded && (
                          <div
                            className="px-4 pb-4 border-t"
                            style={{ borderColor: cfg.border }}
                          >
                            <p className="text-xs mt-3 mb-3 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                              {claim.explanation}
                            </p>

                            {claim.suggestedCitations && claim.suggestedCitations.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>
                                  Suggested Citations:
                                </p>
                                {claim.suggestedCitations.map((cit, idx) => {
                                  const appliedKey = `${claim.id}-${cit.url}`;
                                  const isApplied = appliedCitations.has(appliedKey);
                                  return (
                                    <div
                                      key={`${claim.id}-cit-${idx}`}
                                      className="flex items-start gap-3 px-3 py-2.5 rounded-lg border"
                                      style={{
                                        borderColor: 'var(--border)',
                                        backgroundColor: 'var(--card)',
                                      }}
                                    >
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 mb-0.5">
                                          <p className="text-xs font-medium truncate" style={{ color: 'var(--foreground)' }}>
                                            {cit.title}
                                          </p>
                                          <span
                                            className="text-xs px-1.5 py-0.5 rounded shrink-0"
                                            style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}
                                          >
                                            {cit.type}
                                          </span>
                                        </div>
                                        <p className="text-xs mb-1" style={{ color: 'var(--muted-foreground)' }}>
                                          {cit.author}
                                        </p>
                                        {cit.url && (
                                          <a
                                            href={cit.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-xs transition-opacity hover:opacity-70"
                                            style={{ color: 'var(--primary)' }}
                                          >
                                            <ExternalLink size={10} />
                                            <span className="truncate max-w-xs">{cit.url}</span>
                                          </a>
                                        )}
                                      </div>
                                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                                        <span
                                          className="text-xs font-mono px-1.5 py-0.5 rounded"
                                          style={{
                                            backgroundColor: cit.credibilityScore >= 80 ? '#2C5E3B18' : '#F39C1218',
                                            color: cit.credibilityScore >= 80 ? '#2C5E3B' : '#A06800',
                                          }}
                                        >
                                          {cit.credibilityScore}% credible
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => handleApplyCitation(cit, claim.id)}
                                          disabled={isApplied}
                                          className="text-xs px-2.5 py-1 rounded-lg border font-medium transition-all duration-150 active:scale-95 disabled:opacity-60"
                                          style={{
                                            borderColor: isApplied ? '#2C5E3B44' : 'var(--primary)',
                                            color: isApplied ? '#2C5E3B' : 'var(--primary)',
                                            backgroundColor: isApplied ? '#2C5E3B11' : 'transparent',
                                          }}
                                        >
                                          {isApplied ? '✓ Added' : '+ Add'}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Auto-hyperlinks */}
            {result.autoHyperlinks && result.autoHyperlinks.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--muted-foreground)' }}>
                  Suggested Hyperlinks for SEO ({result.autoHyperlinks.length})
                </h3>
                <div className="space-y-2">
                  {result.autoHyperlinks.map((link, idx) => (
                    <div
                      key={`link-${idx}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg border"
                      style={{ borderColor: 'var(--border)', backgroundColor: 'var(--muted)' }}
                    >
                      <Link2 size={13} style={{ color: 'var(--primary)' }} className="shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium mb-0.5" style={{ color: 'var(--foreground)' }}>
                          &ldquo;{link.phrase}&rdquo;
                        </p>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs transition-opacity hover:opacity-70"
                          style={{ color: 'var(--primary)' }}
                        >
                          <ExternalLink size={10} />
                          {link.sourceTitle}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs mt-2" style={{ color: 'var(--muted-foreground)' }}>
                  These hyperlinks will be reviewed by curators and may be added to the published entry to improve SEO and source credibility.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
